import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { senhaForte } from '@/lib/senha_forte';
import { roleLabel } from '@/lib/roles';
import type { ClienteAdmin, EscritorioAdmin, PapelAdmin } from '@/lib/plataforma_admin';
import { PAPEIS_POR_AMBIENTE } from './UsuariosTable';
import { Blueprint, Button, Input, Select } from '@/components/ui';

interface NovaPessoaFormProps {
  escritorios: EscritorioAdmin[];
  clientes: ClienteAdmin[];
  salvando: boolean;
  /** Resolve `true` só quando o servidor confirmou a criação. */
  onCriar: (payload: {
    email: string;
    senha_provisoria: string;
    role: PapelAdmin;
    display_name?: string | null;
    cpf?: string | null;
    escritorio_id?: string | null;
    banco_id?: string | null;
  }) => Promise<boolean>;
}

type TipoDestino = 'escritorio' | 'cliente';

/**
 * Criação de pessoa em qualquer ambiente — inclusive o primeiro gestor de um // ux-neutrality:allow doc-comment
 * cliente novo (E53.F6). // ux-neutrality:allow doc-comment
 *
 * O destino é exatamente um: escritório **ou** cliente. É a mesma exclusão que o // ux-neutrality:allow doc-comment
 * servidor impõe no contrato — aqui ela vira a forma do formulário, para não // ux-neutrality:allow doc-comment
 * existir estado de tela que o servidor recusaria por construção. // ux-neutrality:allow doc-comment
 */
export function NovaPessoaForm({ escritorios, clientes, salvando, onCriar }: NovaPessoaFormProps) {
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [cpf, setCpf] = useState('');
  const [tipo, setTipo] = useState<TipoDestino>('escritorio');
  const [destino, setDestino] = useState('');
  const [papel, setPapel] = useState<PapelAdmin>('member');
  const [validacao, setValidacao] = useState<string | null>(null);

  const escritorioSelecionado = escritorios.find((item) => item.id === destino);
  const papeisDisponiveis =
    tipo === 'cliente'
      ? PAPEIS_POR_AMBIENTE.banco
      : PAPEIS_POR_AMBIENTE[escritorioSelecionado?.is_platform ? 'plataforma' : 'escritorio'];

  function trocarTipo(novo: TipoDestino) {
    setTipo(novo);
    setDestino('');
    setPapel(novo === 'cliente' ? 'bank_user' : 'member');
  }

  async function handleSubmit(evento: React.FormEvent) {
    evento.preventDefault();
    if (email.trim() === '') {
      setValidacao(ptBR.admin.validationEmailObrigatorio);
      return;
    }
    if (!senhaForte(senha)) {
      setValidacao(ptBR.admin.validationSenhaCurta);
      return;
    }
    if (destino === '') {
      setValidacao(ptBR.admin.validationDestinoObrigatorio);
      return;
    }
    setValidacao(null);
    const criada = await onCriar({
      email: email.trim(),
      senha_provisoria: senha,
      role: papel,
      display_name: nome.trim() === '' ? null : nome.trim(),
      cpf: cpf.trim() === '' ? null : cpf.trim(),
      ...(tipo === 'cliente' ? { banco_id: destino } : { escritorio_id: destino }),
    });
    // Recusa preserva o que foi digitado: a senha provisória não existe em
    // nenhum outro lugar da tela, e limpar o formulário depois de um e-mail
    // duplicado ainda passava a impressão de que a pessoa foi criada.
    if (!criada) return;
    setEmail('');
    setNome('');
    setSenha('');
    setCpf('');
    setDestino('');
  }

  return (
    <Blueprint>
      <form
        onSubmit={(evento) => void handleSubmit(evento)}
        className="flex flex-col gap-sc-2 p-sc-3"
        data-testid="nova-pessoa-form"
      >
        <h2 className="text-heading-sm text-fg">{ptBR.admin.novaPessoaTitle}</h2>
        <p className="text-body-sm text-fg-muted">{ptBR.admin.novaPessoaHint}</p>

        <div className="flex flex-wrap gap-2">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novaPessoaEmail}</span>
            <Input
              value={email}
              onChange={(evento) => setEmail(evento.target.value)}
              data-testid="nova-pessoa-email"
            />
          </label>
          <label className="flex min-w-[180px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novaPessoaNome}</span>
            <Input
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              data-testid="nova-pessoa-nome"
            />
          </label>
          <label className="flex min-w-[180px] flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novaPessoaSenha}</span>
            <Input
              type="password"
              value={senha}
              onChange={(evento) => setSenha(evento.target.value)}
              aria-describedby="nova-pessoa-senha-regra"
              data-testid="nova-pessoa-senha"
            />
          </label>
        </div>
        <p id="nova-pessoa-senha-regra" className="text-caption text-fg-dim">
          {ptBR.senhaForte.regra}
        </p>
        <label className="flex max-w-[260px] flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.novaPessoaCpf}</span>
          <Input
            value={cpf}
            onChange={(evento) => setCpf(evento.target.value)}
            data-testid="nova-pessoa-cpf"
          />
        </label>

        <div className="flex flex-wrap gap-2">
          <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novaPessoaDestinoTipo}</span>
            <Select
              value={tipo}
              onChange={(evento) => trocarTipo(evento.target.value as TipoDestino)}
              data-testid="nova-pessoa-tipo"
            >
              <option value="escritorio">{ptBR.admin.novaPessoaDestinoEscritorio}</option>
              <option value="cliente">{ptBR.admin.novaPessoaDestinoCliente}</option>
            </Select>
          </label>
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novaPessoaDestino}</span>
            <Select
              value={destino}
              onChange={(evento) => setDestino(evento.target.value)}
              data-testid="nova-pessoa-destino"
            >
              <option value="">{ptBR.admin.valorAusente}</option>
              {tipo === 'cliente'
                ? clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </option>
                  ))
                : escritorios.map((escritorio) => (
                    <option key={escritorio.id} value={escritorio.id}>
                      {escritorio.nome}
                    </option>
                  ))}
            </Select>
          </label>
          <label className="flex min-w-[160px] flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novaPessoaPapel}</span>
            <Select
              value={papel}
              onChange={(evento) => setPapel(evento.target.value as PapelAdmin)}
              data-testid="nova-pessoa-papel"
            >
              {papeisDisponiveis.map((opcao) => (
                <option key={opcao} value={opcao}>
                  {roleLabel(opcao)}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <p className="text-body-sm text-fg-muted" data-testid="nova-pessoa-papel-descricao">
          {ptBR.admin.papelDescricao[papel]}
        </p>

        {validacao !== null ? (
          <p
            role="alert"
            data-testid="nova-pessoa-validacao"
            className="text-body-sm text-danger-700"
          >
            {validacao}
          </p>
        ) : null}

        <div>
          <Button
            variant="secondary"
            type="submit"
            disabled={salvando}
            data-testid="nova-pessoa-submit"
          >
            {salvando ? ptBR.admin.novaPessoaEnviando : ptBR.admin.novaPessoaSubmit}
          </Button>
        </div>
      </form>
    </Blueprint>
  );
}
